/**
 * PitchPrint Data Passing Verification & Fix - FIXED VERSION
 * Ensures proper PitchPrint properties are passed during add-to-cart
 * FIXED: Added missing log function definition
 */

(() => {
  'use strict';

  // ⚠️ CRITICAL: Do NOT run on main cart page to avoid conflicts
  if (window.location.pathname === '/cart') {
    console.log('[PitchPrintDataUtils] 🚫 Skipping on main cart page to avoid PitchPrint conflicts');
    return;
  }

  // FIXED: Add missing log function definition
  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[PitchPrintDataUtils]', ...args);

  // =====================================
  // ENHANCED PITCHPRINT DATA HANDLING
  // =====================================

  /**
   * Enhanced function to set PitchPrint data in form
   * Matches PitchPrint's natural behavior exactly
   */
  const setEnhancedPitchPrintData = (projectId, formElement) => {
    if (!projectId || !formElement) return;

    log('📝 Setting enhanced PitchPrint data:', projectId);

    // 1. Main PitchPrint project ID (primary property)
    let pitchPrintInput = formElement.querySelector('input[name="properties[_pitchprint]"]');
    if (!pitchPrintInput) {
      pitchPrintInput = document.createElement('input');
      pitchPrintInput.type = 'hidden';
      pitchPrintInput.name = 'properties[_pitchprint]';
      formElement.appendChild(pitchPrintInput);
    }
    pitchPrintInput.value = projectId;

    // 2. PDF Download property (if enabled in PitchPrint settings)
    let pdfDownloadInput = formElement.querySelector('input[name="properties[_pprint-pdf-download]"]');
    if (!pdfDownloadInput) {
      pdfDownloadInput = document.createElement('input');
      pdfDownloadInput.type = 'hidden';
      pdfDownloadInput.name = 'properties[_pprint-pdf-download]';
      formElement.appendChild(pdfDownloadInput);
    }
    pdfDownloadInput.value = '1'; // Enable PDF download

    // 3. PitchPrint system identifier
    let systemInput = formElement.querySelector('input[name="properties[_pp_system]"]');
    if (!systemInput) {
      systemInput = document.createElement('input');
      systemInput.type = 'hidden';
      systemInput.name = 'properties[_pp_system]';
      systemInput.value = 'unified_v4';
      formElement.appendChild(systemInput);
    }

    log('✅ PitchPrint data set in form (hidden properties only):', {
      projectId,
      properties: [
        'properties[_pitchprint]',
        'properties[_pprint-pdf-download]',
        'properties[_pp_system]'
      ]
    });
  };

  /**
   * Verify PitchPrint data is correctly passed in FormData
   */
  const verifyPitchPrintDataInFormData = (formData, projectId) => {
    log('🔍 Verifying PitchPrint data in FormData...');
    
    const expectedProperties = [
      'properties[_pitchprint]',
      'properties[_pprint-pdf-download]',
      'properties[_pp_system]'
    ];

    let verified = true;
    const report = {};

    expectedProperties.forEach(prop => {
      const value = formData.get(prop);
      report[prop] = value;
      
      if (prop === 'properties[_pitchprint]' && value !== projectId) {
        verified = false;
        log(`❌ Mismatch: Expected ${projectId}, got ${value} for ${prop}`);
      } else if (value) {
        log(`✅ ${prop}: ${value}`);
      } else {
        log(`⚠️ Missing: ${prop}`);
      }
    });

    // Check if main project ID is present
    if (!formData.get('properties[_pitchprint]')) {
      verified = false;
      log('❌ Critical: Main PitchPrint project ID missing from FormData');
    }

    log(verified ? '✅ PitchPrint data verification PASSED' : '❌ PitchPrint data verification FAILED');
    
    return {
      verified,
      report,
      recommendations: verified ? [] : [
        'Ensure setEnhancedPitchPrintData() is called before form submission',
        'Check if form inputs are being properly created',
        'Verify FormData constructor is working correctly'
      ]
    };
  };

  /**
   * Enhanced cart submission with comprehensive PitchPrint data handling
   */
  const submitToCartWithEnhancedPitchPrintData = async (formElement, projectData) => {
    log('🚀 Enhanced cart submission with verified PitchPrint data...');
    
    try {
      // 1. Set enhanced PitchPrint data in form
      if (projectData?.projectId) {
        setEnhancedPitchPrintData(projectData.projectId, formElement);
      }

      // 2. Create FormData with enhanced error handling
      let formData;
      try {
        formData = new FormData(formElement);
      } catch (formDataError) {
        log('❌ FormData constructor failed:', formDataError);
        throw new Error('Failed to create FormData: ' + formDataError.message);
      }

      // 3. Verify PitchPrint data in FormData
      if (projectData?.projectId) {
        const verification = verifyPitchPrintDataInFormData(formData, projectData.projectId);
        
        if (!verification.verified) {
          log('❌ PitchPrint data verification failed, attempting repair...');
          
          // Attempt to repair missing data
          formData.set('properties[_pitchprint]', projectData.projectId);
          formData.set('properties[_pprint-pdf-download]', '1');
          formData.set('properties[_pp_system]', 'unified_v4_repaired');
          
          log('🔧 Attempted FormData repair');
        }
      }

      // 4. Log FormData contents for debugging
      if (window.PITCHPRINT_DEBUG) {
        log('📋 FormData contents:');
        for (let [key, value] of formData.entries()) {
          if (key.includes('properties') || key.includes('pitchprint')) {
            console.log(`  ${key}: ${value}`);
          }
        }
      }

      // 5. Submit to cart with enhanced headers
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
          'X-PitchPrint-System': 'unified_v4'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      // 6. Verify PitchPrint data made it to the cart
      if (projectData?.projectId) {
        await verifyPitchPrintDataInCart(result, projectData.projectId);
      }

      return { success: true, data: result };
      
    } catch (error) {
      log('❌ Enhanced cart submission failed:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Verify PitchPrint data made it to the cart response
   */
  const verifyPitchPrintDataInCart = async (cartData, expectedProjectId) => {
    log('🔍 Verifying PitchPrint data made it to cart...');
    
    if (!cartData || !cartData.properties) {
      log('⚠️ No properties in cart response');
      return;
    }

    const properties = cartData.properties;
    const pitchPrintId = properties._pitchprint || properties.pitchprint;
    
    if (pitchPrintId === expectedProjectId) {
      log('✅ PitchPrint data successfully added to cart:', pitchPrintId);
    } else {
      log('❌ PitchPrint data mismatch in cart:', {
        expected: expectedProjectId,
        found: pitchPrintId,
        allProperties: properties
      });
    }

    // Log all PitchPrint-related properties
    Object.keys(properties).forEach(key => {
      if (key.includes('pitchprint') || key.includes('pprint') || key.includes('pp_')) {
        log(`📋 Cart property: ${key} = ${properties[key]}`);
      }
    });
  };

  /**
   * Test function to verify PitchPrint data flow
   */
  const testPitchPrintDataFlow = async () => {
    log('🧪 Testing PitchPrint data flow...');
    
    const testProjectId = 'test_' + Date.now().toString(36);
    const testForm = document.querySelector('.product-form form, form[action*="/cart/add"]');
    
    if (!testForm) {
      log('❌ No test form found');
      return;
    }

    // Test data setting
    setEnhancedPitchPrintData(testProjectId, testForm);
    
    // Test FormData creation
    try {
      const testFormData = new FormData(testForm);
      const verification = verifyPitchPrintDataInFormData(testFormData, testProjectId);
      
      log('🧪 Test results:', verification);
      
      // Clean up test data
      testForm.querySelectorAll('input[name*="test_"]').forEach(input => input.remove());
      
    } catch (error) {
      log('❌ Test failed:', error);
    }
  };

  /**
   * Monitor cart events to verify PitchPrint data persistence
   */
  const setupPitchPrintDataMonitoring = () => {
    // Listen for cart updated events
    document.addEventListener('cart:updated', (e) => {
      if (e.detail && window.PITCHPRINT_DEBUG) {
        log('📡 Cart updated, checking PitchPrint data...');
        
        // Check if cart data contains PitchPrint properties
        const cartData = e.detail;
        if (cartData.items) {
          cartData.items.forEach((item, index) => {
            if (item.properties) {
              const pitchPrintProps = Object.keys(item.properties).filter(key => 
                key.includes('pitchprint') || key.includes('pprint')
              );
              
              if (pitchPrintProps.length > 0) {
                log(`📋 Item ${index + 1} PitchPrint properties:`, pitchPrintProps);
              }
            }
          });
        }
      }
    });

    log('📡 PitchPrint data monitoring active');
  };

  // =====================================
  // EXPORT FUNCTIONS FOR INTEGRATION
  // =====================================

  // Make functions available globally for debugging
  window.PitchPrintDataUtils = {
    setEnhancedPitchPrintData,
    verifyPitchPrintDataInFormData,
    submitToCartWithEnhancedPitchPrintData,
    verifyPitchPrintDataInCart,
    testPitchPrintDataFlow,
    setupPitchPrintDataMonitoring
  };

  // Auto-setup monitoring
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupPitchPrintDataMonitoring);
  } else {
    setupPitchPrintDataMonitoring();
  }

  log('🔧 PitchPrint data verification utilities loaded');

  // =====================================
  // INTEGRATION WITH UNIFIED ADD-TO-CART
  // =====================================

  /**
   * Drop-in replacement for the submitToCart function in unified add-to-cart system
   * Use this instead of the original submitToCart function
   */
  const enhancedSubmitToCart = async (projectData) => {
    const formElement = document.querySelector('.product-form form, form[action*="/cart/add"]');
    if (!formElement) {
      throw new Error('Add to cart form not found');
    }

    return await submitToCartWithEnhancedPitchPrintData(formElement, projectData);
  };

  // Make available for unified system
  window.enhancedSubmitToCart = enhancedSubmitToCart;

  log('✅ FIXED PitchPrint data verification system loaded successfully');
})();